import React, { useContext, useEffect, useState } from 'react';
import { NewPageWrapper, ServerError, useTitle } from '../../../common';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useGetCustomRole } from '../hooks/useGetCustomRole';
import { Spinner } from '../../../common/components/Spinner/Spinner';
import { ErrorResponse } from '../../../common/types/errorRespose';
import { useBreadcrumbsActions } from '../../../common/components/Breadcrumbs/context/useBreadcrumbsActions';
import { RefreshButton } from '../../../common/components/Breadcrumbs/buttons/RefreshButton/RefreshButton';
import { DeleteButton } from '../../../common/components/Breadcrumbs/buttons/DeleteButton/DeleteButton';
import { Flexbox, Typography } from '@hiiretail/synergy-ui';
import { NotificationsContext } from '../../../common/notifications/Notifications';
import { Card } from '../../../common/components/Card/Card';
import { CustomRolePermissionsTable } from './ui/CustomRolePermissionsTable/CustomRolePermissionsTable';
import { useUpdateCustomRole } from '../hooks/useUpdateCustomRole';
import { CustomRolePermissionDto } from '../../../api-client';
import { useQueryClient } from 'react-query';
import { useAddAttributeModalFacade } from '../hooks/useAddAttributeModalFacade';
import { AddPermissionToCustomRoleModal } from './ui/AddPermissionToCustomRoleModal/AddPermissionToCustomRoleModal';
import { AddCustomRoleAttributeModal } from './ui/modals/AddCustomRoleAttributeModal/AddCustomRoleAttributeModal';
import { useRemoveAttributeFacade } from '../hooks/useRemoveAttributeFacade';
import { filterCustomRolePermissions } from '../utils/filterCustomRolePermissions';
import { DeleteConfirmationModal } from '../../../common/components/DeleteConfirmationModal/DeleteConfirmationModal';
import { useDeleteCustomRoleModalFacade } from '../hooks/useDeleteCustomRoleModalFacade';
import { useDeletePermissionFromCustomRoleModalFacade } from '../hooks/useDeletePermissionFromCustomRoleModalFacade';
import { FixedBindings } from './ui/FixedBindings/FixedBindings';
import { AddFixedBindingModal } from './ui/modals/AddFixedBindingModal/AddFixedBindingModal';
import { I18nKeys } from '../../../i18n/keys';
import { useFeatureFlags } from '@hiiretail/core';

export const CustomRoleDetails: React.FC = () => {
  const [isAddPermissionModalOpen, setIsAddPermissionModalOpen] = useState(false);
  const [isAddFixedBindingModalOpen, setIsAddFixedBindingModalOpen] = useState(false);
  const intl = useIntl();
  const { id } = useParams<{ id: string }>();
  const { updateActions } = useBreadcrumbsActions();
  const { showNotification } = useContext(NotificationsContext);
  const queryClient = useQueryClient();

  // Feature flag for fixed bindings
  const { useFlags } = useFeatureFlags();
  const flags = useFlags();
  const isFixedBindingsEnabled = flags?.releaseIamCustomRoleFixedBindings ?? false;

  const {
    data: customRole,
    error: fetchError,
    refetch: refetchCustomRole,
    isFetching: isFetchingCustomRole,
  } = useGetCustomRole(id as string, {
    retry: false,
    keepPreviousData: false,
  });

  const {
    mutate: updateCustomRole,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdateCustomRole({
    onSuccess: () => {
      showNotification(intl.formatMessage({ id: I18nKeys.CustomRolesUpdatedSuccess }));
      setIsAddPermissionModalOpen(false);
      queryClient.invalidateQueries(`custom-role-${id}`).then();
      queryClient.invalidateQueries(`custom-roles`);
    },
    onError: () => {
      showNotification(intl.formatMessage({ id: I18nKeys.CustomRolesUpdateError }));
    },
  });

  const {
    isCustomRolePermissionDeleteModalOpen,
    permissionToDelete,
    openCustomRolePermissionDeleteModal,
    closeCustomRolePermissionDeleteModal,
  } = useDeletePermissionFromCustomRoleModalFacade();

  const deletePermissionFromCustomRole = (permission: CustomRolePermissionDto) => {
    if (customRole) {
      updateCustomRole(
        {
          id: customRole?.id,
          updateCustomRoleDto: {
            name: customRole.name,
            fixedBindings: customRole.fixedBindings,
            permissions: customRole.permissions
              .map((permission) => ({ id: permission.id, attributes: permission.attributes }))
              .filter((customRolePermission) => customRolePermission.id !== permission.id),
          },
        },
        {
          onSuccess: () => {
            closeCustomRolePermissionDeleteModal();
            showNotification(intl.formatMessage({ id: I18nKeys.CustomRolesUpdatedSuccess }));
            queryClient.invalidateQueries(`custom-role-${id}`).then();
            queryClient.invalidateQueries(`custom-roles`);
          },
        },
      );
    }
  };

  const addPermissionsToCustomRole = (permissions: CustomRolePermissionDto[]) => {
    if (customRole) {
      updateCustomRole({
        id: customRole.id,
        updateCustomRoleDto: {
          name: customRole.name,
          fixedBindings: customRole.fixedBindings,
          permissions: filterCustomRolePermissions(customRole.permissions, permissions),
        },
      });
    }
  };

  const addFixedBinding = (bindings: string[]) => {
    if (customRole) {
      const currentBindings = customRole.fixedBindings || [];
      const newBindings = bindings.filter((binding) => !currentBindings.includes(binding));

      if (newBindings.length > 0) {
        updateCustomRole(
          {
            id: customRole.id,
            updateCustomRoleDto: {
              name: customRole.name,
              fixedBindings: [...currentBindings, ...newBindings],
              permissions: customRole.permissions.map((p) => ({
                id: p.id,
                attributes: p.attributes,
              })),
            },
          },
          {
            onSuccess: () => {
              setIsAddFixedBindingModalOpen(false);
              const message =
                newBindings.length === 1
                  ? intl.formatMessage({ id: I18nKeys.CustomRolesFixedBindingAddedSuccess })
                  : intl.formatMessage(
                      { id: I18nKeys.CustomRolesFixedBindingsAddedSuccess },
                      {
                        count: newBindings.length,
                      },
                    );
              showNotification(message);
              queryClient.invalidateQueries(`custom-role-${id}`).then();
              queryClient.invalidateQueries(`custom-roles`);
            },
          },
        );
      }
    }
  };

  const removeFixedBinding = (binding: string) => {
    if (customRole) {
      const currentBindings = customRole.fixedBindings || [];
      updateCustomRole(
        {
          id: customRole.id,
          updateCustomRoleDto: {
            name: customRole.name,
            fixedBindings: currentBindings.filter((b) => b !== binding),
            permissions: customRole.permissions.map((p) => ({
              id: p.id,
              attributes: p.attributes,
            })),
          },
        },
        {
          onSuccess: () => {
            showNotification(intl.formatMessage({ id: I18nKeys.CustomRolesFixedBindingRemoved }));
            queryClient.invalidateQueries(`custom-role-${id}`).then();
            queryClient.invalidateQueries(`custom-roles`);
          },
        },
      );
    }
  };

  useTitle(customRole ? `Custom Roles | ${customRole.id}` : 'Custom Roles');

  const {
    openAddAttributeModal,
    closeAddAttributeModal,
    isAddAttributeModalOpen,
    isAddingAttribute,
    selectedPermission,
    addAttribute,
  } = useAddAttributeModalFacade(id as string);

  const { isRemovingAttribute, removeAttribute } = useRemoveAttributeFacade(
    id as string,
    customRole,
  );

  const {
    isDeleteCustomRoleModalOpen,
    toggleDeleteCustomRoleModalOpen,
    deleteCustomRole,
    isDeletingCustomRole,
  } = useDeleteCustomRoleModalFacade();

  useEffect(() => {
    updateActions(
      <>
        <RefreshButton onClick={refetchCustomRole} permission="iam.custom-role.get" />
        <DeleteButton
          title="Delete role"
          onClick={toggleDeleteCustomRoleModalOpen}
          permission="iam.custom-role.delete"
          disabled={isDeletingCustomRole}
        />
      </>,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeletingCustomRole]);

  return (
    <NewPageWrapper>
      {customRole && (
        <>
          <AddPermissionToCustomRoleModal
            onClose={() => setIsAddPermissionModalOpen(false)}
            isOpen={isAddPermissionModalOpen}
            isAdding={isUpdating}
            error={updateError}
            addPermissionToCustomRole={addPermissionsToCustomRole}
            permissionsToFilterOut={customRole?.permissions.map((permission) => permission.id)}
          />
          {isFixedBindingsEnabled && (
            <AddFixedBindingModal
              open={isAddFixedBindingModalOpen}
              onClose={() => setIsAddFixedBindingModalOpen(false)}
              onAdd={addFixedBinding}
              isAdding={isUpdating}
              existingBindings={customRole?.fixedBindings || []}
            />
          )}
          <AddCustomRoleAttributeModal
            open={isAddAttributeModalOpen}
            onClose={closeAddAttributeModal}
            customRole={customRole}
            selectedPermission={selectedPermission}
            addAttribute={addAttribute}
            isAdding={isAddingAttribute}
          />
          <DeleteConfirmationModal
            title={customRole.name as string}
            entity="custom role"
            isOpen={isDeleteCustomRoleModalOpen}
            onClose={toggleDeleteCustomRoleModalOpen}
            isDeleting={isDeletingCustomRole}
            deleteEntity={() => deleteCustomRole(id as string)}
            dataTestId="delete-custom-role-confirmation-modal"
          />
          <DeleteConfirmationModal
            title={permissionToDelete?.id as string}
            entity="custom role permission"
            isOpen={isCustomRolePermissionDeleteModalOpen}
            onClose={closeCustomRolePermissionDeleteModal}
            isDeleting={isUpdating}
            deleteEntity={() =>
              deletePermissionFromCustomRole(permissionToDelete as CustomRolePermissionDto)
            }
            dataTestId="delete-custom-role-permission-confirmation-modal"
          />
        </>
      )}

      {/*Loading*/}
      {isFetchingCustomRole ? (
        <Spinner dataTestId="custom-role-details-spinner" />
      ) : (
        <>
          {/*Error (not 404)*/}
          {fetchError && (fetchError as ErrorResponse | null)?.response?.status !== 404 && (
            <ServerError error={(fetchError as ErrorResponse | null)?.message} />
          )}

          {!fetchError && !!customRole && (
            <Flexbox container gutter={4} style={{ height: '100%', width: '100%' }}>
              <Flexbox item style={{ width: '25%' }}>
                <Flexbox container direction="column" gutter={3}>
                  <Card title={customRole.id}>
                    <Typography>{customRole.name}</Typography>
                  </Card>
                  {isFixedBindingsEnabled && (
                    <FixedBindings
                      fixedBindings={customRole.fixedBindings}
                      isLoading={isUpdating || isFetchingCustomRole}
                      onAddClick={() => setIsAddFixedBindingModalOpen(true)}
                      onRemoveClick={removeFixedBinding}
                    />
                  )}
                </Flexbox>
              </Flexbox>
              <Flexbox item style={{ width: '75%', zIndex: 99 }}>
                <CustomRolePermissionsTable
                  isLoading={isUpdating || isFetchingCustomRole || isRemovingAttribute}
                  permissions={customRole.permissions}
                  openAddAttributeModal={openAddAttributeModal}
                  onAddPermissionBtnClick={() => setIsAddPermissionModalOpen(true)}
                  isRemovingAttribute={isRemovingAttribute}
                  removeAttribute={removeAttribute}
                  openCustomRolePermissionDeleteModal={openCustomRolePermissionDeleteModal}
                />
              </Flexbox>
            </Flexbox>
          )}
        </>
      )}
    </NewPageWrapper>
  );
};
